import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useUser, useClerk, Show } from "@clerk/react";
import { Menu, ChevronDown, LogOut, X, ArrowRight, Trash2 } from "lucide-react";
import { IconClapperboard, IconPlayBounce, IconStarBurst, IconSparkleWand, IconFilmPlay, IconCompass, IconFolder, IconCoin, IconLifebuoy } from "@/components/ui/cartoon-icons";
import { useUserStore } from "@/stores/user-store";
import { CTLogo } from "@/components/ui/ct-logo";
import { AuthModal } from "@/components/ui/auth-modal";
import { DeleteAccountModal } from "@/components/ui/delete-account-modal";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export function TopBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      setMobileMenuOpen(false);
    }
  }, [location.pathname]);

  const toggleMenu = useCallback(() => setMobileMenuOpen((v) => !v), []);

  return (
    <div className="relative px-4 md:px-6" style={{ paddingTop: "max(1rem, env(safe-area-inset-top, 1rem))" }}>
      <header className="max-w-5xl mx-auto flex items-center justify-between px-3 md:px-5 h-[52px] md:h-[60px] rounded-[14px] md:rounded-[18px] bg-white border-2 border-[#2a2342] shadow-[3px_3px_0_rgba(42,35,66,0.18)]">
        <div className="flex items-center gap-6">
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 -ml-1 rounded-xl text-[#241d3a] hover:bg-[#241d3a]/[0.06] transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="size-5 text-[#241d3a]" strokeWidth={3} /> : <Menu className="size-5 text-[#241d3a]" strokeWidth={3} />}
          </button>

          <Link to="/" className="flex items-center gap-2.5 hover:opacity-90 transition-opacity">
            <CTLogo className="size-8" />
            <span className="hidden sm:inline text-[16px] tracking-tight text-[#241d3a] uppercase" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>
              CartoonTale
            </span>
          </Link>

          <nav className="items-center gap-1 hidden md:flex">
            <NavDropdown />
            <NavLink href="/explore" label="Explore" />
            <NavLink href="/assets" label="My Assets" />
            <NavLink href="/billing" label="Pricing" />
          </nav>
        </div>

        <Actions />
      </header>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </div>
  );
}

function NavLink({ href, label }: { href: string; label: string }) {
  const location = useLocation();
  const isActive = href === "/"
    ? location.pathname === "/"
    : location.pathname.startsWith(href);

  return (
    <Link
      to={href}
      className={cn(
        "px-4 py-2 rounded-xl text-[14px] font-bold transition-colors",
        isActive ? "text-[#241d3a] underline underline-offset-4 decoration-2" : "text-[#241d3a]",
      )}
    >
      {label}
    </Link>
  );
}

const CREATE_ITEMS = [
  { href: "/create-cartoon", label: "Generate Video", Icon: IconClapperboard },
  { href: "/create-character", label: "Create Character", Icon: IconStarBurst },
  { href: "/extend-cartoon", label: "Extend Video", Icon: IconFilmPlay },
];

function NavDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isActive = CREATE_ITEMS.some((item) => location.pathname.startsWith(item.href));

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1.5 px-4 py-2 rounded-xl text-[14px] font-bold transition-colors text-[#241d3a]",
          isActive && "underline underline-offset-4 decoration-2",
        )}
      >
        Create
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-3 w-64 rounded-2xl bg-white border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] p-2 animate-modal-in z-50">
          {CREATE_ITEMS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[14px] font-bold text-[#241d3a] hover:bg-[#241d3a]/[0.06] transition-colors"
            >
              <item.Icon className="size-5" />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Actions() {
  const { isLoaded: isClerkLoaded } = useAuth();
  const { credits, isLoading: isUserLoading } = useUserStore();
  const [showAuth, setShowAuth] = useState(false);

  if (!isClerkLoaded) {
    return <div className="h-10 w-32 rounded-xl bg-[#241d3a]/[0.04] animate-pulse" />;
  }

  return (
    <div className="flex items-center gap-2.5">
      <Show when="signed-out">
        <button
          onClick={() => setShowAuth(true)}
          className="hidden md:flex items-center gap-1.5 px-5 py-2 rounded-[12px] border-2 border-[#2a2342] bg-white text-[13px] font-extrabold text-[#241d3a] transition-all shadow-[2px_2px_0_rgba(42,35,66,0.16)] hover:shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)]"
        >
          Log in
        </button>
        <button
          onClick={() => setShowAuth(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 md:px-5 md:py-2 rounded-[10px] md:rounded-[12px] border-2 md:border-2 border-[#2a2342] bg-[#b8a5f0] text-[12px] md:text-[13px] font-extrabold text-[#241d3a] transition-all shadow-[2px_2px_0_rgba(42,35,66,0.16)] hover:shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:bg-[#c8b8f5] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)]"
        >
          Get started <ArrowRight className="size-3.5" />
        </button>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </Show>
      <Show when="signed-in">
        <div className="flex items-stretch rounded-[12px] border-2 border-[#2a2342] bg-white text-[12px] font-extrabold text-[#241d3a] shadow-[2px_2px_0_rgba(42,35,66,0.16)] transition-all hover:shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px]">
          <Link to="/billing" className="flex items-center gap-2 pl-3 pr-2.5 py-2">
            <IconCoin className="size-5" />
            {isUserLoading ? "..." : credits.toLocaleString()}
          </Link>
          <Link to="/billing" className="flex items-center px-3 py-2 border-l-2 border-[#2a2342] bg-[#b8a5f0] rounded-r-[9px] transition-colors hover:bg-[#c8b8f5]">
            Buy
          </Link>
        </div>
        <Link to="/create-cartoon">
          <button className="flex items-center gap-1.5 px-3 py-1.5 md:px-5 md:py-2 rounded-[10px] md:rounded-[12px] border-2 md:border-2 border-[#2a2342] bg-[#b8a5f0] text-[12px] md:text-[13px] font-extrabold text-[#241d3a] transition-all shadow-[2px_2px_0_rgba(42,35,66,0.16)] hover:shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:bg-[#c8b8f5] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)]">
            Create <ArrowRight className="size-3.5" />
          </button>
        </Link>
        <ProfileButton />
      </Show>
    </div>
  );
}

function ProfileButton() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleDeleteAccount = useCallback(async () => {
    if (!user?.id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      setShowDeleteModal(false);
      signOut();
    } catch (err) {
      console.error("Failed to delete account:", err);
      setIsDeleting(false);
    }
  }, [user?.id, signOut]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative size-8 md:size-9 rounded-xl border-2 md:border-2 border-[#2a2342] transition-all overflow-hidden shadow-[1px_1px_0_rgba(42,35,66,0.16)]"
      >
        <img src={user?.imageUrl} alt={user?.fullName || "Profile"} className="size-full object-cover" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-3 w-56 rounded-2xl bg-white border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] p-2 animate-modal-in z-50">
          <div className="px-3.5 py-2.5 border-b-2 border-[#241d3a]/10 mb-1">
            <p className="text-[13px] font-extrabold text-[#241d3a] truncate">{user?.fullName || user?.username}</p>
            <p className="text-[11px] text-[#241d3a]/45 font-semibold truncate">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
          <Link to="/assets" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-[#241d3a] hover:bg-[#241d3a]/[0.06] transition-colors">
            My Assets
          </Link>
          <Link to="/billing" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-[#241d3a] hover:bg-[#241d3a]/[0.06] transition-colors">
            Tokens
          </Link>
          <Link to="/support" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-[#241d3a] hover:bg-[#241d3a]/[0.06] transition-colors">
            Support
          </Link>
          <div className="h-px bg-[#241d3a]/10 mx-2 my-1" />
          <button onClick={() => { setOpen(false); signOut(); }} className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-red-500 hover:bg-red-50 transition-colors">
            <LogOut className="size-4" /> Sign out
          </button>
          <button onClick={() => { setOpen(false); setShowDeleteModal(true); }} className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-red-400/60 hover:text-red-500 hover:bg-red-50 transition-colors">
            <Trash2 className="size-4" /> Delete Account
          </button>
        </div>
      )}

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeleting}
      />
    </div>
  );
}

const MOBILE_SECTIONS = [
  {
    title: "Create",
    items: [
      { href: "/create-cartoon", label: "Generate Video", Icon: IconClapperboard },
      { href: "/create-character", label: "Create Character", Icon: IconStarBurst },
      { href: "/extend-cartoon", label: "Extend Video", Icon: IconFilmPlay },
    ],
  },
  {
    title: "Browse",
    items: [
      { href: "/explore", label: "Explore", Icon: IconCompass },
      { href: "/assets", label: "My Assets", Icon: IconFolder },
    ],
  },
  {
    title: "Account",
    items: [
      { href: "/billing", label: "Tokens & Pricing", Icon: IconCoin },
      { href: "/support", label: "Support", Icon: IconLifebuoy },
    ],
  },
];

function MobileMenu({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
      document.body.style.overflow = "hidden";
    } else if (mounted) {
      setClosing(true);
      document.body.style.overflow = "";
      const timer = setTimeout(() => { setMounted(false); setClosing(false); }, 300);
      return () => clearTimeout(timer);
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-[100] bg-[#241d3a]/35",
          closing ? "animate-mobile-backdrop-out" : "animate-mobile-backdrop-in",
        )}
        onClick={onClose}
      />

      <div
        className={cn(
          "fixed inset-x-0 top-0 bottom-0 z-[101] bg-[#f3f0fb] overflow-y-auto",
          closing ? "animate-mobile-menu-out" : "animate-mobile-menu-in",
        )}
      >
        <div className="px-5 pb-8" style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top, 1.25rem))", paddingBottom: "max(2rem, env(safe-area-inset-bottom, 2rem))" }}>
          <div className="flex items-center justify-between mb-8">
            <Link to="/" onClick={onClose} className="flex items-center gap-2.5">
              <CTLogo className="size-9" />
              <span className="text-[17px] tracking-tight text-[#241d3a] uppercase" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>
                CartoonTale
              </span>
            </Link>
            <button
              onClick={onClose}
              className="size-10 rounded-xl bg-[#241d3a]/[0.06] border border-[#241d3a]/15 flex items-center justify-center text-[#241d3a]/60 hover:bg-[#241d3a]/[0.08] hover:text-[#241d3a] active:bg-[#241d3a]/[0.04] transition-all"
            >
              <X className="size-5" />
            </button>
          </div>

          {MOBILE_SECTIONS.map((section, si) => (
            <div key={section.title} className={cn(si > 0 && "mt-6")}>
              <p className="text-[11px] font-extrabold text-[#241d3a]/45 uppercase tracking-widest mb-3 px-1">{section.title}</p>
              <div className="grid grid-cols-2 gap-2.5">
                {section.items.map((item) => {
                  const isActive = item.href === "/" ? location.pathname === "/" : location.pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={onClose}
                      className={cn(
                        "flex flex-col gap-2.5 p-4 rounded-2xl border transition-all",
                        isActive
                          ? "bg-[#241d3a]/[0.09] border-[#241d3a]/15"
                          : "bg-[#241d3a]/[0.04] border-[#241d3a]/10 active:bg-[#241d3a]/[0.08]",
                      )}
                    >
                      <item.Icon className="size-8" />
                      <span className="text-[13px] font-extrabold text-[#241d3a] leading-tight">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          <Show when="signed-out">
            <div className="mt-8 space-y-2.5">
              <button
                onClick={() => { onClose(); setShowAuth(true); }}
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl border-2 border-[#2a2342] bg-[#b8a5f0] text-[15px] font-extrabold text-[#241d3a] shadow-[2px_2px_0_rgba(42,35,66,0.16)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all"
              >
                Get Started Free <ArrowRight className="size-4" />
              </button>
              <button
                onClick={() => { onClose(); setShowAuth(true); }}
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[#241d3a]/[0.06] border border-[#241d3a]/15 text-[15px] font-extrabold text-[#241d3a] active:bg-[#241d3a]/[0.04] transition-all"
              >
                Log In
              </button>
            </div>
          </Show>
        </div>
      </div>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}
